import { unzipSync } from 'fflate';

import type { TranslationKey } from '@/i18n';
import { readNumber, readRecord } from '@/lib/readers';
import type { TemplateSize, TemplateType } from './types';
import type { TemplatePreviewStatus } from './useTemplates';

/** Every unpacked entry of one archive, keyed by its normalised path. */
export type ArchiveFiles = Map<string, Uint8Array>;

const MIME_BY_EXTENSION: Record<string, string> = {
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  js: 'text/javascript',
  json: 'application/json',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  wav: 'audio/wav',
  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  otf: 'font/otf',
};

const EXTERNAL_REFERENCE = /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i;
const TEXT_DECODER = new TextDecoder('utf-8');

/** Strips the `./` and `/` prefixes zip tools disagree on. */
export function normalizeArchivePath(name: string): string {
  return name.replace(/\\/g, '/').replace(/^(\.\/|\/)+/, '');
}

/** Returns the lowercased extension of a path. */
export function extensionOf(path: string): string {
  return path.slice(path.lastIndexOf('.') + 1).toLowerCase();
}

/** Returns the MIME type of a path, read from its extension. */
export function mimeTypeOf(path: string): string {
  return MIME_BY_EXTENSION[extensionOf(path)] ?? 'application/octet-stream';
}

/** Returns true for `http(s)://`, `data:`, protocol-relative and fragment references. */
export function isExternalReference(reference: string): boolean {
  return EXTERNAL_REFERENCE.test(reference.trim());
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Joins a reference onto the directory of the referencing file, collapsing `.` and `..`. */
export function resolveArchivePath(from: string, reference: string): string {
  const clean = decodeSegment(reference.trim().split(/[?#]/)[0] ?? '');
  const base = clean.startsWith('/') ? [] : from.split('/').slice(0, -1);
  const segments: string[] = [...base];
  for (const segment of clean.split('/')) {
    if (segment === '..') segments.pop();
    else if (segment !== '' && segment !== '.') segments.push(segment);
  }
  return segments.join('/');
}

/** Returns the entry a relative reference points at, or null when it is external or missing. */
export function lookupReference(files: ArchiveFiles, from: string, reference: string): string | null {
  if (reference.trim() === '' || isExternalReference(reference)) return null;
  const path = resolveArchivePath(from, reference);
  return files.has(path) ? path : null;
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

/** Returns the bytes of one entry as a `data:` URL. */
export function toDataUrl(path: string, bytes: Uint8Array): string {
  return `data:${mimeTypeOf(path)};base64,${toBase64(bytes)}`;
}

/** Returns the bytes as UTF-8 text. */
export function decodeText(bytes: Uint8Array): string {
  return TEXT_DECODER.decode(bytes);
}

const AD_SIZE_META = /<meta\b[^>]*\bname\s*=\s*["']ad\.size["'][^>]*>/i;
const GWD_PAGE_TAG = /<gwd-page\b[^>]*>/i;
const GWD_AD_METADATA = /<script\b[^>]*type\s*=\s*["']text\/gwd-admetadata["'][^>]*>([\s\S]*?)<\/script\s*>/i;
const WIDTH = /width\s*=\s*(\d+)/i;
const HEIGHT = /height\s*=\s*(\d+)/i;

function sized(width: number, height: number): TemplateSize | null {
  return width > 0 && height > 0 ? { width, height } : null;
}

/** Returns the size from `<meta name="ad.size" content="width=300,height=250">`. */
function fromAdSizeMeta(html: string): TemplateSize | null {
  const tag = AD_SIZE_META.exec(html)?.[0];
  if (!tag) return null;
  return sized(Number(WIDTH.exec(tag)?.[1]), Number(HEIGHT.exec(tag)?.[1]));
}

/** Returns the size from `<gwd-page data-gwd-width="300px" data-gwd-height="600px">`. */
function fromGwdPage(html: string): TemplateSize | null {
  const tag = GWD_PAGE_TAG.exec(html)?.[0];
  if (!tag) return null;
  const attribute = (name: string): number =>
    parseInt(new RegExp(`\\sdata-gwd-${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(tag)?.[1] ?? '', 10);
  return sized(attribute('width'), attribute('height'));
}

/** Returns the JSON manifest Google Web Designer writes into the page, or null. */
export function readGwdAdMetadata(html: string): Record<string, unknown> | null {
  const body = GWD_AD_METADATA.exec(html)?.[1];
  if (!body) return null;
  try {
    return readRecord(JSON.parse(body));
  } catch {
    return null;
  }
}

function fromGwdAdMetadata(html: string): TemplateSize | null {
  const properties = readRecord(readGwdAdMetadata(html)?.creativeProperties);
  return (
    sized(readNumber(properties.maxWidth), readNumber(properties.maxHeight)) ??
    sized(readNumber(properties.minWidth), readNumber(properties.minHeight))
  );
}

/** Returns the frame size an assembled page declares, from the IAB meta tag, the GWD
 *  page element or the GWD manifest; null when none does. */
export function detectArchiveSize(html: string): TemplateSize | null {
  return fromAdSizeMeta(html) ?? fromGwdPage(html) ?? fromGwdAdMetadata(html);
}

const CSS_URL = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;

/** Replaces every `url(...)` that names an archive entry with a `data:` URL; the rest stay as they are. */
export function inlineCssUrls(css: string, cssPath: string, files: ArchiveFiles): string {
  return css.replace(CSS_URL, (match, _quote: string, reference: string) => {
    const path = lookupReference(files, cssPath, reference);
    return path ? `url("${toDataUrl(path, files.get(path) as Uint8Array)}")` : match;
  });
}

const BASE_TAG = /<base\b[^>]*>/gi;
const LINK_TAG = /<link\b[^>]*>/gi;
const SCRIPT_ELEMENT = /<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi;
const STYLE_ELEMENT = /<style\b([^>]*)>([\s\S]*?)<\/style\s*>/gi;
const ANY_TAG = /<([a-z][a-z0-9-]*)\b[^>]*>/gi;
const MEDIA_ATTRIBUTES = ['src', 'poster'] as const;

/** The attributes of each tag that refer to an archive entry, with the Google Web Designer elements. */
const REFERENCE_ATTRIBUTES_BY_TAG: Record<string, readonly string[]> = {
  img: MEDIA_ATTRIBUTES,
  video: MEDIA_ATTRIBUTES,
  audio: MEDIA_ATTRIBUTES,
  source: MEDIA_ATTRIBUTES,
  track: MEDIA_ATTRIBUTES,
  'gwd-video': ['sources', 'poster'],
  'gwd-audio': ['sources'],
  'gwd-image': ['source'],
};

/** The attributes that have a list of references, separated by commas. */
const LIST_ATTRIBUTES = new Set(['sources']);

function attributePattern(name: string): RegExp {
  return new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');
}

function attributeOf(tag: string, name: string): string | null {
  const match = attributePattern(name).exec(tag);
  return match ? (match[1] ?? match[2] ?? match[3] ?? '') : null;
}

function withAttribute(tag: string, name: string, value: string): string {
  return tag.replace(attributePattern(name), ` ${name}="${value.replace(/"/g, '&quot;')}"`);
}

function withoutAttribute(tag: string, name: string): string {
  return tag.replace(attributePattern(name), '');
}

interface Inliner {
  entry: string;
  files: ArchiveFiles;
}

/** Returns the `data:` URL of a referenced entry, or the reference when it is external or missing. */
function dataUrlFor({ entry, files }: Inliner, reference: string): string {
  const path = lookupReference(files, entry, reference);
  return path ? toDataUrl(path, files.get(path) as Uint8Array) : reference;
}

function inlineSrcset(inliner: Inliner, srcset: string): string {
  return srcset
    .split(',')
    .map((candidate) => {
      const [url = '', ...descriptor] = candidate.trim().split(/\s+/);
      return [dataUrlFor(inliner, url), ...descriptor].join(' ');
    })
    .join(', ');
}

function inlineLink(inliner: Inliner, tag: string): string {
  const rel = (attributeOf(tag, 'rel') ?? '').toLowerCase();
  const href = attributeOf(tag, 'href');
  const path = href === null ? null : lookupReference(inliner.files, inliner.entry, href);
  if (!path) return tag;
  if (rel.includes('stylesheet')) {
    const css = decodeText(inliner.files.get(path) as Uint8Array);
    return `<style>${inlineCssUrls(css, path, inliner.files)}</style>`;
  }
  return rel.includes('icon') ? withAttribute(tag, 'href', toDataUrl(path, inliner.files.get(path) as Uint8Array)) : tag;
}

function inlineScript(inliner: Inliner, attributes: string, body: string): string {
  const src = attributeOf(attributes, 'src');
  const path = src === null ? null : lookupReference(inliner.files, inliner.entry, src);
  if (!path) return `<script${attributes}>${body}</script>`;
  const code = decodeText(inliner.files.get(path) as Uint8Array).replace(/<\/script/gi, '<\\/script');
  return `<script${withoutAttribute(attributes, 'src')}>${code}</script>`;
}

function inlineReferenceList(inliner: Inliner, value: string): string {
  return value
    .split(',')
    .map((reference) => dataUrlFor(inliner, reference.trim()))
    .join(',');
}

function inlineTag(inliner: Inliner, tag: string, name: string): string {
  let next = tag;
  const attributes = REFERENCE_ATTRIBUTES_BY_TAG[name.toLowerCase()];
  if (attributes) {
    for (const attribute of attributes) {
      const value = attributeOf(next, attribute);
      if (value === null) continue;
      const inlined = LIST_ATTRIBUTES.has(attribute)
        ? inlineReferenceList(inliner, value)
        : dataUrlFor(inliner, value);
      next = withAttribute(next, attribute, inlined);
    }
    const srcset = attributeOf(next, 'srcset');
    if (srcset !== null) next = withAttribute(next, 'srcset', inlineSrcset(inliner, srcset));
  }
  const style = attributeOf(next, 'style');
  return style === null ? next : withAttribute(next, 'style', inlineCssUrls(style, inliner.entry, inliner.files));
}

/** Rewrites one HTML page and puts each archive reference in the page itself. */
export function inlineHtmlReferences(html: string, entry: string, files: ArchiveFiles): string {
  const inliner: Inliner = { entry, files };
  return html
    .replace(BASE_TAG, '')
    .replace(LINK_TAG, (tag) => inlineLink(inliner, tag))
    .replace(SCRIPT_ELEMENT, (_match, attributes: string, body: string) => inlineScript(inliner, attributes, body))
    .replace(
      STYLE_ELEMENT,
      (_match, attributes: string, css: string) => `<style${attributes}>${inlineCssUrls(css, entry, files)}</style>`,
    )
    .replace(ANY_TAG, (tag, name: string) => inlineTag(inliner, tag, name));
}

const MEGABYTE = 1024 * 1024;

/** What one archive may hold before it is refused. */
export interface ArchiveLimits {
  compressedBytes: number;
  unpackedBytes: number;
  entries: number;
}

/** The limits of an archive the preview opens. */
export const PREVIEW_LIMITS: ArchiveLimits = {
  compressedBytes: 80 * MEGABYTE,
  unpackedBytes: 160 * MEGABYTE,
  entries: 400,
};

/** The limits of an archive the import opens. */
export const IMPORT_LIMITS: ArchiveLimits = {
  compressedBytes: 250 * MEGABYTE,
  unpackedBytes: 500 * MEGABYTE,
  entries: 2000,
};

export type PreviewFailure = 'tooLarge' | 'noPage' | 'unreadable';

export type PreviewResult =
  | { ok: true; html: string; size: TemplateSize | null }
  | { ok: false; reason: PreviewFailure };

/** One archive opened: every entry, the page that starts it, and that page's source. */
export type UnpackResult =
  | { ok: true; files: ArchiveFiles; entry: string; html: string }
  | { ok: false; reason: PreviewFailure };

const PAGE_EXTENSIONS = new Set(['html', 'htm']);

function depthOf(path: string): number {
  return path.split('/').length;
}

function isIndexPage(path: string): boolean {
  return /(^|\/)index\.html?$/i.test(path);
}

/** Returns the shallowest index page, else the shallowest HTML page. A tie goes to the first by name. */
export function selectEntryPage(paths: Iterable<string>): string | null {
  const pages = [...paths].filter((path) => PAGE_EXTENSIONS.has(extensionOf(path)));
  const ranked = pages.sort(
    (left, right) =>
      Number(isIndexPage(right)) - Number(isIndexPage(left)) ||
      depthOf(left) - depthOf(right) ||
      left.localeCompare(right),
  );
  return ranked[0] ?? null;
}

class ArchiveTooLargeError extends Error {}

/** Unpacks every file. It stops before decompression when an entry or size limit is crossed. */
function unpack(archive: Uint8Array, limits: ArchiveLimits): ArchiveFiles {
  let entries = 0;
  let unpacked = 0;
  const unzipped = unzipSync(archive, {
    filter: (file) => {
      if (file.name.endsWith('/')) return false;
      entries += 1;
      unpacked += file.originalSize;
      if (entries > limits.entries || unpacked > limits.unpackedBytes) throw new ArchiveTooLargeError();
      return true;
    },
  });
  return new Map(Object.entries(unzipped).map(([name, bytes]) => [normalizeArchivePath(name), bytes]));
}

/** Opens one archive and finds its start page. It never throws: a bad archive returns a refusal. */
export function unpackArchive(archive: Uint8Array, limits: ArchiveLimits = PREVIEW_LIMITS): UnpackResult {
  if (archive.byteLength > limits.compressedBytes) return { ok: false, reason: 'tooLarge' };

  let files: ArchiveFiles;
  try {
    files = unpack(archive, limits);
  } catch (error) {
    return { ok: false, reason: error instanceof ArchiveTooLargeError ? 'tooLarge' : 'unreadable' };
  }

  const entry = selectEntryPage(files.keys());
  if (!entry) return { ok: false, reason: 'noPage' };

  return { ok: true, files, entry, html: decodeText(files.get(entry) as Uint8Array) };
}

/** Returns one HTML5 archive as a self-contained document, with the size it declares. */
export function assembleTemplatePreview(
  archive: Uint8Array,
  limits: ArchiveLimits = PREVIEW_LIMITS,
): PreviewResult {
  const opened = unpackArchive(archive, limits);
  if (!opened.ok) return opened;

  const { files, entry, html } = opened;
  return { ok: true, html: inlineHtmlReferences(html, entry, files), size: detectArchiveSize(html) };
}

/** The frame size when the archive declared none. */
export const DEFAULT_TEMPLATE_SIZE: TemplateSize = { width: 300, height: 250 };

/** Returns the size a `width:height` format names, or null when it names none. */
function formatSize(format: string): TemplateSize | null {
  const [width, height] = format.split(':').map(Number);
  return width && height ? { width, height } : null;
}

/** Returns the CSS `aspect-ratio` value: the declared size, else the format, else the default frame. */
export function aspectRatioOf(format: string, size: TemplateSize | null): string {
  const frame = size ?? formatSize(format) ?? DEFAULT_TEMPLATE_SIZE;
  return `${frame.width} / ${frame.height}`;
}

/** Returns the ratio of a CSS `aspect-ratio` value, or null when it has none. */
export function ratioOf(value: string): number | null {
  const [width = 0, height = 0] = value.split('/').map((part) => Number(part.trim()));
  return width > 0 && height > 0 ? width / height : null;
}

/** The shape of each thumbnail in the list view. */
export const LIST_THUMB_RATIO = '16 / 9';

/** Longest edge of the proportional thumb of a size preset, in pixels. */
export const SIZE_THUMB_MAX = 26;

/** The message key of each preview that cannot show. */
export const PREVIEW_FAILURE_KEYS: Partial<Record<TemplatePreviewStatus, TranslationKey>> = {
  unavailable: 'brandHub.template.previewUnavailable',
  tooLarge: 'brandHub.template.previewTooLarge',
  noPage: 'brandHub.template.previewNoPage',
};

export const ARCHIVE_MAX_BYTES = 100 * 1024 * 1024;
export const ARCHIVE_ACCEPT = 'application/zip,application/x-zip-compressed,.zip';

/** The kind of template an uploaded archive creates. */
export const ARCHIVE_TEMPLATE_TYPE: TemplateType = 'html5';

/** The message key of each refusal of an uploaded archive. */
export const ARCHIVE_REFUSALS: Record<number, TranslationKey> = {
  400: 'brandHub.template.invalidArchive',
};

/** Returns the listed format whose ratio is nearest the given size. */
export function closestFormat(width: number, height: number, formats: readonly string[]): string {
  const distance = (format: string) => {
    const size = formatSize(format);
    return size ? Math.abs(size.width / size.height - width / height) : Number.POSITIVE_INFINITY;
  };
  return [...formats].sort((left, right) => distance(left) - distance(right))[0] ?? '';
}
